/**
 * Admin header "ServiceM8 client match review" badge — Oct 2026.
 *
 * Sits next to `AdminPresenceChip`/`FulfilmentOutstandingBadge` in the
 * admin portal header (`PortalShell.jsx`) and polls
 * `GET /api/admin/integrations/servicem8/client-match-review-count`
 * every 30 seconds. Self-hides when the count is 0 so it never adds
 * noise on a quiet day — only shows up when Megan actually has
 * something to resolve.
 *
 * Click -> jumps to the ServiceM8 Sync Monitor (Settings -> ServiceM8
 * Scheduling), where the review is actually resolved.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserSearch } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_INTERVAL_MS = 30_000;

export function ServiceM8ReviewBadge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/integrations/servicem8/client-match-review-count");
        if (mounted) setCount(data.count || 0);
      } catch { /* silent — badge just stays stale/hidden */ }
    };
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => { mounted = false; clearInterval(id); };
  }, [isAdmin]);

  if (!isAdmin || count === 0) return null;

  return (
    <button
      type="button"
      data-testid="sm8-review-count-badge"
      onClick={() => navigate("/admin/settings/servicem8")}
      title={`${count} booking${count === 1 ? "" : "s"} waiting on a ServiceM8 client-match review`}
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50
                 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
    >
      <UserSearch className="h-3.5 w-3.5" />
      {count} client match{count === 1 ? "" : "es"} to review
    </button>
  );
}
